import React, { Fragment, useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";
import MainWrapper from "../../../MainWrapper";
import { Link, useNavigate } from "react-router-dom";
import Icon from "react-icons-kit";
import { ic_arrow_back } from 'react-icons-kit/md';
import { Table, Button, Form, InputGroup } from "react-bootstrap";
import Loader from "../../../layouts/Loader";
import axios from "axios";
import { API_URL } from "../../../../config";
import {
  CUSTOMER_PRIORITY_LIST_REQUEST,
  CUSTOMER_PRIORITY_REMOVE_REQUEST,
  CUSTOMER_PRIORITY_STATE_CHANGE
} from "../../../../_utils/constants/CustomerPriority";
import { STORE_INFO_REQUEST } from "../../../../_utils/constants/admin/StoreInfo";
import ImportPriority from "./ImportPriority";
import { toast } from 'react-toastify';
import { Modal } from "react-bootstrap";

const CustomerPriorityList = ({
  auth: { auth, isAuthenticated },
  CustomerPriority: { priorityList, isLoading, isImported, isRemoved, importResult, errors },
  StoreInfo: { storeInfo },
  fetchCustomerPriorityList,
  fetchStoreInfo,
  removeCustomerPriority,
  changeReducerState,
}) => {

  // state declarations
  const [priorityListing, setPriorityListing] = useState([])
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [confirmRemoveModal, setConfirmRemoveModal] = useState(false)
  const [selectedStoreId, setSelectedStoreId] = useState("")
  const [isEnabled, setIsEnabled] = useState(true)
  const [emitSchedulerFields, setEmitSchedulerFields] = useState(true)
  const [windowDays, setWindowDays] = useState(60)
  const [orderThreshold, setOrderThreshold] = useState(7)
  const [settingsSaving, setSettingsSaving] = useState(false)
  const [settingsLastModified, setSettingsLastModified] = useState(null)
  const [isExporting, setIsExporting] = useState(false)
  const navigate = useNavigate()

  // Fetch runtime settings once on mount
  useEffect(() => {
    if (!auth || !auth[0] || !auth[0].token) return
    axios
      .get(`${API_URL}/User/api/CustomerPriority/GetSettings`, {
        headers: { Authorization: `Bearer ${auth[0].token}` }
      })
      .then((res) => {
        const row = (res.data && res.data.result && res.data.result[0]) || null
        if (row) {
          setIsEnabled(row.isEnabled !== false)
          setEmitSchedulerFields(row.emitSchedulerFields !== false)
          setWindowDays(row.newCustomerWindowDays ?? 60)
          setOrderThreshold(row.newCustomerOrderThreshold ?? 7)
          setSettingsLastModified(row.modifiedOn || null)
        }
      })
      .catch((err) => {
        // On failure, keep defaults. Do not block the rest of the page.
        console.warn("Could not load Customer Priority settings:", err.message)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSaveSettings = () => {
    if (!auth || !auth[0] || !auth[0].token) return
    const days = parseInt(windowDays, 10)
    const threshold = parseInt(orderThreshold, 10)
    if (!Number.isFinite(days) || days < 1) {
      toast.error("New Customer Window must be a positive integer.")
      return
    }
    if (!Number.isFinite(threshold) || threshold < 1) {
      toast.error("Order Threshold must be a positive integer.")
      return
    }
    setSettingsSaving(true)
    axios
      .post(
        `${API_URL}/User/api/CustomerPriority/SaveSettings?userId=${auth[0].userId || 0}`,
        {
          isEnabled: isEnabled,
          emitSchedulerFields: emitSchedulerFields,
          newCustomerWindowDays: days,
          newCustomerOrderThreshold: threshold,
        },
        { headers: { Authorization: `Bearer ${auth[0].token}` } }
      )
      .then((res) => {
        const row = (res.data && res.data.result && res.data.result[0]) || null
        if (row) {
          setIsEnabled(row.isEnabled !== false)
          setEmitSchedulerFields(row.emitSchedulerFields !== false)
          setWindowDays(row.newCustomerWindowDays)
          setOrderThreshold(row.newCustomerOrderThreshold)
          setSettingsLastModified(row.modifiedOn || null)
          toast.success(`Settings saved. Master=${row.isEnabled ? 'ON' : 'OFF'}, Scheduler=${row.emitSchedulerFields ? 'ON' : 'OFF'}, Window=${row.newCustomerWindowDays}d, Threshold=${row.newCustomerOrderThreshold}.`)
        } else {
          toast.success("Settings saved.")
        }
      })
      .catch((err) => {
        const msg = (err.response && err.response.data && err.response.data.responsestatus) || err.message
        toast.error(`Save failed: ${msg}`)
      })
      .finally(() => setSettingsSaving(false))
  }

  // Check the authentication and fetch apis
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login")
    }
    if (isAuthenticated) {
      fetchStoreInfo(auth[0].token)
    }
  }, [isAuthenticated, auth])

  // Fetch priority list when store is selected
  useEffect(() => {
    if (isAuthenticated && selectedStoreId) {
      fetchCustomerPriorityList(auth[0].token, selectedStoreId)
    }
  }, [selectedStoreId])

  // Set first store as default when store info loads
  useEffect(() => {
    if (storeInfo && storeInfo.length > 0 && !selectedStoreId) {
      setSelectedStoreId(storeInfo[0].storeId)
    }
  }, [storeInfo])

  // Check and set the priority list state
  useMemo(() => {
    if (priorityList && priorityList.length > 0) {
      setPriorityListing(priorityList)
    } else {
      setPriorityListing([])
    }
  }, [priorityList])

  // use effect function called after api success
  useMemo(() => {
    if (isImported === true) {
      toast.success("Customer priorities imported successfully.");
      setImportModalOpen(false);
      changeReducerState();
      if (isAuthenticated && selectedStoreId) {
        fetchCustomerPriorityList(auth[0].token, selectedStoreId)
      }
    }
    if (isRemoved === true) {
      toast.success("All customer priorities removed successfully.");
      changeReducerState();
      if (isAuthenticated && selectedStoreId) {
        fetchCustomerPriorityList(auth[0].token, selectedStoreId)
      }
    }
  }, [isImported, isRemoved])

  // Function to handle error from response
  useMemo(() => {
    if (errors && errors.data && errors.data.result && errors.data.result.length > 0) {
      toast.error(errors.data.result[0])
      changeReducerState();
    }
  }, [errors])

  // function to show import modal
  const showImportModal = () => {
    setImportModalOpen(true)
  }

  // function to hide import modal
  const hideImportModal = () => {
    setImportModalOpen(false)
    changeReducerState();
  }

  // function to show confirm remove modal
  const showConfirmRemoveModal = () => {
    setConfirmRemoveModal(true)
  }

  // function to hide confirm remove modal
  const hideConfirmRemoveModal = () => {
    setConfirmRemoveModal(false)
  }

  // function called for remove all priorities
  const handleConfirmRemoveSubmit = () => {
    removeCustomerPriority({
      "storeId": selectedStoreId,
      "token": auth[0].token
    });
    hideConfirmRemoveModal();
  }

  // function to handle store change
  const handleStoreChange = (e) => {
    setSelectedStoreId(e.target.value)
  }

  const handleExportExcel = async () => {
    if (!selectedStoreId) {
      toast.error("Please select a store before exporting.");
      return;
    }
    setIsExporting(true);
    try {
      const response = await axios.get(
        `${API_URL}/User/api/CustomerPriority/ExportCustomerPriority?storeId=${encodeURIComponent(selectedStoreId)}`,
        {
          headers: { Authorization: `Bearer ${auth[0].token}` },
          responseType: 'blob',
        }
      );
      const disposition = response.headers['content-disposition'] || '';
      const match = disposition.match(/filename[^;=\n]*=["']?([^"';\n]+)/i);
      const filename = (match && match[1].trim()) || `CustomerPriority_${selectedStoreId}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error("Export failed. Please try again.");
      }
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <Fragment>
      <MainWrapper>
        <Loader loading={isLoading}>
          <div className="container">
            <div className="sub-header mt-5 mb-3">
              <h2>Customer Priority</h2>
              <Link className="btn btn-primary me-1" to="/admin"><Icon icon={ic_arrow_back} />Back</Link>
              <ImportPriority
                importModalOpen={importModalOpen}
                showImportModal={showImportModal}
                hideImportModal={hideImportModal}
                importStoreId={selectedStoreId}
              />
              <Button
                className="btn-sm ms-1"
                variant="success"
                onClick={handleExportExcel}
                disabled={isExporting || isLoading || !selectedStoreId}
              >
                {isExporting ? 'Exporting…' : 'Export All to Excel'}
              </Button>
              {priorityListing.length > 0 && (
                <Button className="btn-sm ms-1" variant="danger" onClick={showConfirmRemoveModal}>
                  Clear All
                </Button>
              )}
            </div>

            <div className="mb-3">
              <Form.Group style={{ maxWidth: '300px' }}>
                <Form.Label>Filter by Store</Form.Label>
                <Form.Select value={selectedStoreId} onChange={handleStoreChange}>
                  <option value="">Select Store</option>
                  {storeInfo && storeInfo.length > 0 && storeInfo.map((store) => (
                    <option key={store.storeId} value={store.storeId}>{store.storeName}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </div>

            <div className="mb-4 p-3 border rounded" style={{ background: '#f8f9fa' }}>
              <h5 className="mb-2">Customer Priority Settings (global)</h5>
              <p className="text-muted mb-3" style={{ fontSize: '0.9em' }}>
                All four controls persist in the DB and take effect on the next request — no API restart. Precedence: DB value → appsettings.json → hardcoded default.
              </p>
              <div className="d-flex gap-4 align-items-center flex-wrap mb-3">
                <Form.Check
                  type="switch"
                  id="cp-master-toggle"
                  label={<span><strong>Feature enabled (master)</strong>{isEnabled ? '' : ' — all behavior off, UI & BC sort revert to legacy'}</span>}
                  checked={isEnabled}
                  onChange={(e) => setIsEnabled(e.target.checked)}
                />
                <Form.Check
                  type="switch"
                  id="cp-scheduler-toggle"
                  label={<span><strong>Emit scheduler fields</strong>{emitSchedulerFields ? '' : ' — priorityNumber & isNewCustomer blanked on the wire (non-breaking)'}</span>}
                  checked={emitSchedulerFields}
                  onChange={(e) => setEmitSchedulerFields(e.target.checked)}
                  disabled={!isEnabled}
                />
              </div>
              <div className="d-flex gap-3 align-items-end flex-wrap">
                <Form.Group>
                  <Form.Label>New Customer Window (days)</Form.Label>
                  <InputGroup style={{ width: '180px' }}>
                    <Form.Control
                      type="number"
                      min="1"
                      value={windowDays}
                      onChange={(e) => setWindowDays(e.target.value)}
                    />
                    <InputGroup.Text>days</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Order Threshold</Form.Label>
                  <InputGroup style={{ width: '160px' }}>
                    <Form.Control
                      type="number"
                      min="1"
                      value={orderThreshold}
                      onChange={(e) => setOrderThreshold(e.target.value)}
                    />
                    <InputGroup.Text>orders</InputGroup.Text>
                  </InputGroup>
                </Form.Group>
                <Button
                  variant="primary"
                  onClick={handleSaveSettings}
                  disabled={settingsSaving}
                  style={{ height: '38px' }}
                >
                  {settingsSaving ? 'Saving…' : 'Save Settings'}
                </Button>
                {settingsLastModified && (
                  <span className="text-muted small ms-2" style={{ alignSelf: 'center' }}>
                    Last changed: {new Date(settingsLastModified).toLocaleString()}
                  </span>
                )}
              </div>
            </div>

            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Priority #</th>
                  <th>Property Name</th>
                  <th>Management Company</th>
                  <th>Store</th>
                </tr>
              </thead>
              <tbody>
                {priorityListing.length > 0 ? (
                  priorityListing.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.priorityNumber}</td>
                      <td>{item.propertyName}</td>
                      <td>{item.managementCompany}</td>
                      <td>{item.storeName}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center">No records found</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </Loader>

        <Modal show={confirmRemoveModal} size="md" onHide={hideConfirmRemoveModal}>
          <Modal.Header closeButton>
            <div className="modal-title h4">Clear All Priorities</div>
          </Modal.Header>
          <Modal.Body className="text-center">
            <h5 className="mb-0">Are you sure you want to remove all customer priorities for this store?</h5>
            <p className="text-danger">
              <span>*</span> This action cannot be undone.
            </p>
          </Modal.Body>
          <Modal.Footer className="justify-content-center">
            <Button variant="danger" onClick={handleConfirmRemoveSubmit}>
              Yes
            </Button>
            <Button variant="secondary" onClick={hideConfirmRemoveModal}>
              No
            </Button>
          </Modal.Footer>
        </Modal>
      </MainWrapper>
    </Fragment>
  )
}

const mapStateToProps = ({ auth, CustomerPriority, StoreInfo }) => {
  return {
    auth, CustomerPriority, StoreInfo
  }
}
const mapDispatchToProps = (dispatch) => {
  return {
    fetchCustomerPriorityList: (token, storeId) => dispatch({ type: CUSTOMER_PRIORITY_LIST_REQUEST, token, storeId }),
    fetchStoreInfo: (token) => dispatch({ type: STORE_INFO_REQUEST, token }),
    removeCustomerPriority: (data) => dispatch({ type: CUSTOMER_PRIORITY_REMOVE_REQUEST, payload: data }),
    changeReducerState: () => dispatch({ type: CUSTOMER_PRIORITY_STATE_CHANGE }),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CustomerPriorityList);
